/**
 * IO Services — admin catalogue editor (Mantine).
 *
 * Lets the platform owner curate the marketplace catalogue shown
 * to studios on the customer app's /io-services page. Soft-delete
 * via `isActive` toggle. Re-runnable seed button on empty state
 * for one-click bootstrap (also grants app_owner claim if missing,
 * via the `bootstrapPlatformServices` Cloud Function).
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Image,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconDownload,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { httpsCallable } from 'firebase/functions';
import { useMemo, useState } from 'react';

import { auth, functions } from '@/lib/firebase';
import {
  PLATFORM_SERVICE_CATEGORIES,
  createPlatformService,
  deletePlatformService,
  getPlatformServiceCategoryConfig,
  updatePlatformService,
  type PlatformService,
  type PlatformServiceCategory,
} from '@/lib/platformServices';
import { DEFAULT_PLATFORM_SERVICES } from '@/lib/platformServicesSeed';
import { usePlatformServices } from '@/lib/usePlatformServices';

type BootstrapResponse = {
  ok: true;
  claimSet: boolean;
  claimAlreadyPresent: boolean;
  created: number;
  updated: number;
  total: number;
};

type FormMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: PlatformService };

export function IOServicesAdmin() {
  const [categoryFilter, setCategoryFilter] = useState<PlatformServiceCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const [formMode, setFormMode] = useState<FormMode>({ kind: 'closed' });
  const [bootstrapping, setBootstrapping] = useState(false);

  const { data, loading } = usePlatformServices({
    includeInactive: true,
    category: categoryFilter === 'all' ? null : categoryFilter,
    searchText: search,
  });

  const stats = useMemo(() => {
    let active = 0;
    let inactive = 0;
    for (const s of data) (s.isActive ? active++ : inactive++);
    return { active, inactive };
  }, [data]);

  async function runBootstrap() {
    const user = auth.currentUser;
    if (!user) return;
    const isReseed = data.length > 0;
    const message = isReseed
      ? `Re-seed will UPDATE existing default services (matched by seedKey) and INSERT any missing ones from the bundled ${DEFAULT_PLATFORM_SERVICES.length}-service catalogue. Custom services you added will NOT be touched.`
      : `Seed will create ${DEFAULT_PLATFORM_SERVICES.length} starter services (Floor Protection at ₹9/sqft, Pest Control, Website, Photography, etc.) so the marketplace isn't empty.`;
    if (!confirm(message + ' Continue?')) return;
    setBootstrapping(true);
    try {
      const callable = httpsCallable<undefined, BootstrapResponse>(
        functions,
        'bootstrapPlatformServices',
      );
      const result = await callable();
      const r = result.data;
      // Force a token refresh so the new app_owner claim (if just
      // granted) is visible in the current session.
      await user.getIdToken(true);
      notifications.show({
        color: 'green',
        title: 'Catalogue seeded',
        message:
          `${r.created} created, ${r.updated} updated (${r.total} total).` +
          (r.claimSet ? ' app_owner role granted.' : ''),
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Seed failed',
        message: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setBootstrapping(false);
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>IO Services — catalogue</Title>
          <Text size="sm" c="dimmed">
            {stats.active} active · {stats.inactive} disabled · shown to studios on{' '}
            <code>/io-services</code>
          </Text>
        </div>
        <Group gap="xs">
          <Button
            variant="default"
            leftSection={<IconDownload size={14} />}
            onClick={runBootstrap}
            loading={bootstrapping}
          >
            {data.length > 0
              ? 'Re-seed defaults'
              : `Seed ${DEFAULT_PLATFORM_SERVICES.length} starter services`}
          </Button>
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={() => setFormMode({ kind: 'create' })}
          >
            Add service
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Group>
        <Select
          data={[
            { value: 'all', label: 'All categories' },
            ...PLATFORM_SERVICE_CATEGORIES.map((c) => ({ value: c.key, label: c.label })),
          ]}
          value={categoryFilter}
          onChange={(v) => setCategoryFilter((v as PlatformServiceCategory | 'all') ?? 'all')}
          w={220}
        />
        <TextInput
          placeholder="Search title, tagline, features…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          flex={1}
        />
      </Group>

      {loading ? (
        <Text c="dimmed">Loading…</Text>
      ) : data.length === 0 ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="blue"
          title="Catalogue is empty"
        >
          Click <strong>Seed {DEFAULT_PLATFORM_SERVICES.length} starter services</strong>{' '}
          above to populate the marketplace with 19 services (Floor Protection at ₹9/sqft,
          Pest Control, Website, Photography, and 16 more). Each one is fully editable
          afterwards. The seed call also grants you the <code>app_owner</code> role if
          you don't already have it.
        </Alert>
      ) : (
        <Stack gap={8}>
          {data.map((s) => (
            <AdminServiceRow
              key={s.id}
              service={s}
              onEdit={() => setFormMode({ kind: 'edit', item: s })}
              onToggleActive={() =>
                updatePlatformService(s.id, { isActive: !s.isActive }).catch((e) =>
                  notifications.show({
                    color: 'red',
                    title: 'Toggle failed',
                    message: e instanceof Error ? e.message : 'Unknown error',
                  }),
                )
              }
            />
          ))}
        </Stack>
      )}

      {formMode.kind !== 'closed' ? (
        <ServiceFormModal
          mode={formMode}
          onClose={() => setFormMode({ kind: 'closed' })}
        />
      ) : null}
    </Stack>
  );
}

// ───── Row ────────────────────────────────────────────────────

function AdminServiceRow({
  service,
  onEdit,
  onToggleActive,
}: {
  service: PlatformService;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  const cfg = getPlatformServiceCategoryConfig(service.category);
  const heroUri = service.images?.[0] ?? service.imageUrl;
  return (
    <Card withBorder p="sm" opacity={service.isActive ? 1 : 0.55}>
      <Group wrap="nowrap" align="center" gap="md">
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 8,
            background: cfg.tintBg,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {heroUri ? (
            <Image src={heroUri} alt="" h={48} w={48} fit="cover" />
          ) : null}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text fw={600} truncate>
              {service.title}
            </Text>
            <Badge color="gray" variant="light" size="xs">
              {cfg.label}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed" truncate>
            {service.tagline || service.shortDescription}
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            {service.priceLabel}
            {service.priceNote ? ` · ${service.priceNote}` : ''}
            {service.deliveryTime ? ` · ${service.deliveryTime}` : ''}
          </Text>
        </div>
        <Switch
          checked={service.isActive}
          onChange={onToggleActive}
          label="Active"
          size="sm"
        />
        <Button
          variant="default"
          size="xs"
          leftSection={<IconPencil size={12} />}
          onClick={onEdit}
        >
          Edit
        </Button>
      </Group>
    </Card>
  );
}

// ───── Form modal ────────────────────────────────────────────

function ServiceFormModal({
  mode,
  onClose,
}: {
  mode: { kind: 'create' } | { kind: 'edit'; item: PlatformService };
  onClose: () => void;
}) {
  const user = auth.currentUser;
  const editing = mode.kind === 'edit' ? mode.item : null;

  const [title, setTitle] = useState(editing?.title ?? '');
  const [category, setCategory] = useState<PlatformServiceCategory>(
    editing?.category ?? 'site',
  );
  const [tagline, setTagline] = useState(editing?.tagline ?? '');
  const [shortDescription, setShortDescription] = useState(
    editing?.shortDescription ?? '',
  );
  const [longDescription, setLongDescription] = useState(
    editing?.longDescription ?? '',
  );
  const [priceLabel, setPriceLabel] = useState(editing?.priceLabel ?? 'On request');
  const [priceNote, setPriceNote] = useState(editing?.priceNote ?? '');
  const [deliveryTime, setDeliveryTime] = useState(editing?.deliveryTime ?? '');
  const [featuresText, setFeaturesText] = useState(
    (editing?.features ?? []).join('\n'),
  );
  const [imagesText, setImagesText] = useState(
    (editing?.images ?? (editing?.imageUrl ? [editing.imageUrl] : [])).join('\n'),
  );
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState<number>(editing?.sortOrder ?? 0);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!title.trim() || !shortDescription.trim() || !priceLabel.trim()) {
      notifications.show({
        color: 'red',
        title: 'Missing fields',
        message: 'Title, short description, and price label are required.',
      });
      return;
    }
    setBusy(true);
    try {
      const features = featuresText.split('\n').map((s) => s.trim()).filter(Boolean);
      const images = imagesText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 5);
      if (mode.kind === 'create') {
        if (!user) throw new Error('Not signed in.');
        await createPlatformService({
          title,
          category,
          tagline: tagline.trim() || undefined,
          shortDescription,
          longDescription: longDescription.trim() || undefined,
          priceLabel,
          priceNote: priceNote.trim() || undefined,
          deliveryTime: deliveryTime.trim() || undefined,
          features: features.length ? features : undefined,
          images: images.length ? images : undefined,
          isActive,
          sortOrder,
          createdBy: user.uid,
        });
      } else {
        await updatePlatformService(mode.item.id, {
          title,
          category,
          tagline,
          shortDescription,
          longDescription,
          priceLabel,
          priceNote,
          deliveryTime,
          features,
          images,
          isActive,
          sortOrder,
        });
      }
      notifications.show({
        color: 'green',
        title: mode.kind === 'create' ? 'Service added' : 'Service updated',
        message: title,
      });
      onClose();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (mode.kind !== 'edit') return;
    if (!confirm(`Delete "${mode.item.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await deletePlatformService(mode.item.id);
      notifications.show({
        color: 'green',
        title: 'Service deleted',
        message: mode.item.title,
      });
      onClose();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Delete failed',
        message: e instanceof Error ? e.message : 'Unknown error',
      });
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={() => !busy && onClose()}
      title={mode.kind === 'create' ? 'New service' : 'Edit service'}
      size="lg"
    >
      <Stack gap="sm">
        <TextInput
          label="Title"
          required
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          placeholder="e.g. Floor Protection"
        />
        <Select
          label="Category"
          required
          data={PLATFORM_SERVICE_CATEGORIES.map((c) => ({
            value: c.key,
            label: c.label,
          }))}
          value={category}
          onChange={(v) => setCategory((v as PlatformServiceCategory) ?? 'site')}
        />
        <TextInput
          label="Tagline (hook line under the title)"
          value={tagline}
          onChange={(e) => setTagline(e.currentTarget.value)}
          placeholder="e.g. One call. We take care of everything."
        />
        <Textarea
          label="Short description"
          required
          autosize
          minRows={2}
          value={shortDescription}
          onChange={(e) => setShortDescription(e.currentTarget.value)}
          placeholder="One-line teaser shown on the catalogue card."
        />
        <Textarea
          label="Long description"
          autosize
          minRows={3}
          value={longDescription}
          onChange={(e) => setLongDescription(e.currentTarget.value)}
          placeholder="Paragraph shown inside the order modal."
        />
        <TextInput
          label="Price label"
          required
          value={priceLabel}
          onChange={(e) => setPriceLabel(e.currentTarget.value)}
          placeholder="On request · ₹9 / sqft · Starting ₹2,000"
        />
        <TextInput
          label="Price note (sub-detail)"
          value={priceNote}
          onChange={(e) => setPriceNote(e.currentTarget.value)}
          placeholder="Material + installation included · Excl. GST"
        />
        <TextInput
          label="Delivery time"
          value={deliveryTime}
          onChange={(e) => setDeliveryTime(e.currentTarget.value)}
          placeholder="Within 24 hrs · 2–3 days · Per schedule"
        />
        <Textarea
          label="Features (one per line)"
          autosize
          minRows={3}
          value={featuresText}
          onChange={(e) => setFeaturesText(e.currentTarget.value)}
          placeholder={'Free site visit\nSame-day quote\nWarranty for 30 days'}
        />
        <Textarea
          label="Images (one URL per line, up to 5)"
          autosize
          minRows={3}
          value={imagesText}
          onChange={(e) => setImagesText(e.currentTarget.value)}
          placeholder={'https://…/hero.jpg\nhttps://…/work-1.jpg'}
        />
        {imagesText.trim() ? (
          <Group gap="xs">
            {imagesText
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean)
              .slice(0, 5)
              .map((url, i) => (
                <Image
                  key={`${url}-${i}`}
                  src={url}
                  alt={`Preview ${i + 1}`}
                  w={56}
                  h={56}
                  fit="cover"
                  radius="sm"
                  fallbackSrc="https://placehold.co/56?text=?"
                />
              ))}
          </Group>
        ) : null}
        <Group grow>
          <NumberInput
            label="Sort order"
            value={sortOrder}
            onChange={(v) =>
              setSortOrder(typeof v === 'number' ? v : Number(v) || 0)
            }
          />
          <Switch
            label="Visible to studios"
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
            mt={24}
          />
        </Group>
        <Group justify="space-between" mt="md">
          {mode.kind === 'edit' ? (
            <Button
              variant="default"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={remove}
              disabled={busy}
            >
              Delete
            </Button>
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              {mode.kind === 'create' ? 'Add service' : 'Save changes'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
